import React, { useEffect } from 'react';
import { Dialog } from '../../components/ui/Dialog';
import {
  Loader2,
  Phone,
  Globe,
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Building2,
  Link2,
  Loader,
  MessageCircle,
  CreditCard,
  Calendar,
  Mail,
  Briefcase,
  DollarSign
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useTxnLink } from '../../store/hooks/useTxnLink';
import { TxnLinkCard } from '../../components/TxnLinkCard';
import { PreApprovalLogDetailResponse } from '../../services/preApprovalLogs';
import { PreApprovalLog } from '../../services/preApprovalLogs';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';


interface PreApprovalLogDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  logData: PreApprovalLogDetailResponse | null;
  selectedLog: PreApprovalLog | null;
  isLoading: boolean;
}

const PreApprovalLogDetailModal: React.FC<PreApprovalLogDetailModalProps> = ({
  isOpen,
  onClose,
  logData,
  selectedLog,
  isLoading
}) => {
  // Format date for display
  const { user } = useAppSelector((state) => state.auth);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';

    try {
      // Check if the date is already in DD-MM-YYYY HH:MM:SS AM/PM format
      if (/^\d{2}-\d{2}-\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+[AP]M$/.test(dateString)) {
        return dateString;
      }

      // Handle ISO date format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.log("Invalid date encountered:", dateString);

        // Try special handling for specific invalid formats
        if (dateString === 'Invalid Date') {
          return selectedLog?.createdAt || 'N/A';
        }

        return dateString; // Return the original string if truly invalid
      }

      // Format the valid date
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString; // Return the original string if parsing fails
    }
  };

  const formatDateCustomer = (dateString?: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format platform for display
  const formatPlatform = (platform?: string) => {
    if (!platform) return "N/A";

    switch (platform.toLowerCase()) {
      case 'merchant':
        return 'Merchant';
      case 'gateway-partner':
      case 'gateway':
        return 'Gateway';
      case 'customer':
        return 'Website';
      default:
        return platform;
    }
  };

  // Render status badge with appropriate color
  const renderStatusBadge = (response?: any) => {
    if (!response) return <span className="text-xs text-gray-500">N/A</span>;

    // Handle Zype error response - show as rejected
    if (response.error) {
      return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center bg-red-100 text-red-800`}>
          <XCircle size={14} className="mr-1" /> rejected
        </span>
      );
    }

    // Regular status handling
    let icon = null;
    let bgColor = '';
    let textColor = '';
    let displayStatus = response.status || 'N/A';

    // Normalize Zype specific statuses
    if (displayStatus === 'ACCEPT') {
      displayStatus = 'approved';
    } else if (displayStatus === 'PRE_APPROVAL_OFFER_ALREADY_GENERATED') {
      displayStatus = 'approved';
    } else if (displayStatus === 'REJECT') {
      displayStatus = 'rejected';
    }

    switch (displayStatus.toLowerCase()) {
      case 'approved':
        icon = <CheckCircle size={14} className="mr-1" />;
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'rejected':
        icon = <XCircle size={14} className="mr-1" />;
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'pending':
        icon = <AlertTriangle size={14} className="mr-1" />;
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      default:
        icon = <AlertTriangle size={14} className="mr-1" />;
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center ${bgColor} ${textColor}`}>
        {icon} {displayStatus}
      </span>
    );
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender?: string) => {
    //const { user } = useAppSelector(state => state.auth);
    // Don't render anything if role is cleverpeuser
    if (user?.role === UserRole.CLEVERPE_USER) return null;
    if (!lender) return <span className="text-xs text-gray-500">N/A</span>;

    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'cashe-ev':
        bgColor = 'bg-green-500';
        textColor = 'text-white';
        break;
      case 'caprion':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'general':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  // Get platform-specific icon
  const getPlatformIcon = (platform?: string) => {
    if (!platform) return <Globe size={20} />;

    switch (platform.toLowerCase()) {
      case 'merchant':
        return <Building2 size={20} />;
      case 'gateway-partner':
      case 'gateway':
        return <CreditCard size={20} />;
      case 'customer':
        return <User size={20} />;
      default:
        return <Globe size={20} />;
    }
  };

  // Get status-specific color for header
  const getStatusColor = (response?: any) => {
    if (!response) return "bg-gray-100";

    // Handle Zype error case - treat like rejected
    if (response.error) {
      return "bg-gradient-to-r from-red-50 to-red-100 border-red-200";
    }

    // Handle normal status
    const status = response.status?.toLowerCase();

    // Normalize Zype specific statuses
    if (status === 'accept' || status === 'pre_approval_offer_already_generated') {
      return "bg-gradient-to-r from-green-50 to-green-100 border-green-200";
    }
    if (status === 'reject') {
      return "bg-gradient-to-r from-red-50 to-red-100 border-red-200";
    }
    switch (status) {
      case 'approved':
        return "bg-gradient-to-r from-green-50 to-green-100 border-green-200";
      case 'rejected':
        return "bg-gradient-to-r from-red-50 to-red-100 border-red-200";
      default:
        return "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200";
    }
  };

  // Get status-specific icon for header
  const getStatusIcon = (response?: any) => {
    if (!response) return <AlertTriangle size={24} className="text-gray-400" />;

    // Handle Zype error as rejected
    if (response.error) {
      return <XCircle size={24} className="text-red-500" />;
    }

    const status = response.status?.toLowerCase();

    // Normalize Zype specific statuses
    if (status === 'accept' || status === 'pre_approval_offer_already_generated') {
      return <CheckCircle size={24} className="text-green-500" />;
    }

    switch (status) {
      case 'approved':
        return <CheckCircle size={24} className="text-green-500" />;
      case 'rejected':
        return <XCircle size={24} className="text-red-500" />;
      default:
        return <AlertTriangle size={24} className="text-yellow-500" />;
    }
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getEmploymentTypeLabel = (type?: number) => {
    switch (type) {
      case 1: return 'Salaried';
      case 2: return 'Unemployed';
      case 3: return 'Self-employed';
      default: return 'N/A';
    }
  };

  const preApproval = selectedLog?.preApprovalResponse;
  const isCaprion = selectedLog?.lender?.toLowerCase() === 'caprion';

  // CAPRION sends approvedLoanAmount, other lenders send amount
  const displayAmount = isCaprion
    ? Number(preApproval?.approvedLoanAmount) || 0
    : typeof preApproval?.amount === 'number'
      ? preApproval.amount
      : 0;

  const {
    isRegeneratingLink, generatedLink, linkExpiry, linkCopied,
    setGeneratedLink, resetLink, generate, copyLink,
  } = useTxnLink();

  // Clear the link when a different log is opened
  useEffect(() => { resetLink(); }, [selectedLog?._id]);   // import useEffect from 'react'

  const canRegenerate =
    user?.role !== UserRole.CLEVERPE_USER &&
    isCaprion &&
    !!preApproval &&
    !preApproval.error;

  const handleRegenerateLink = () => {
    const d: any = logData?.data;
    if (!d) return;

    const payload = {
      amount: displayAmount,
      mobileNumber: Number(d.customerMobileNo),
      name: d.customerName,
      email: d.customerEmail,
      pan: d.customerPan,
      dob: d.customerdob ? new Date(d.customerdob).toISOString().slice(0, 10) : '',
      employmentType: String(d.customerEmploymentType),
      declaredIncome: d.customerMonthlyIncome,
      callbackUrl: d.callbackUrl,
      productDetails: d.productDetails,
      merchantId: d.merchantCode ?? d.merchantId,
      pincode: Number(d.customerPincode ?? d.pincode),
    };

    const partnerId = d.partnerId?._id || d.partnerId?.id || d.partnerId;
    generate(payload, partnerId);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Pre-approval Log Details"
      size="lg"
    >
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
          <span className="ml-2 text-gray-600">Loading details...</span>
        </div>
      ) : !logData?.success ? (
        <div className="py-8 text-center">
          <AlertTriangle className="mx-auto w-12 h-12 text-yellow-500" />
          <p className="mt-4 text-lg text-gray-600">
            Failed to load log details. Please try again.
          </p>
        </div>
      ) : (
        <div>
          {/* Enhanced Header with Status */}
          <div className={`px-6 py-4 mb-4 rounded-lg border ${selectedLog?.preApprovalResponse ?
            getStatusColor(selectedLog?.preApprovalResponse) : 'bg-gray-100 border-gray-200'}`}>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="p-2 mr-3 rounded-full bg-white/80">
                  {getPlatformIcon(selectedLog?.platform)}
                </div>
                <div>
                  <div className="flex items-center mb-1">
                    <h3 className="mr-2 text-lg text-gray-800 font-gilroy-bold">
                      {selectedLog?.customerName || 'Customer'}
                    </h3>
                    {selectedLog?.lender && renderLenderBadge(selectedLog.lender)}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone size={14} className="mr-1" />
                    {selectedLog?.customerMobileNo || 'N/A'}
                    <span className="mx-2">•</span>
                    <Globe size={14} className="mr-1" />
                    {formatPlatform(selectedLog?.platform)}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end">
                {selectedLog?.preApprovalResponse && (
                  <div className="mb-1">
                    {renderStatusBadge(selectedLog.preApprovalResponse)}
                  </div>
                )}
                {preApproval && !preApproval.error && displayAmount > 0 && (
                  <div className="text-xl text-gray-800 font-gilroy-bold">
                    ₹{displayAmount.toLocaleString('en-IN')}
                  </div>
                )}

                {canRegenerate && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateLink}
                    disabled={isRegeneratingLink}
                    className="flex items-center gap-2 mt-2 bg-white"
                  >
                    {isRegeneratingLink
                      ? <Loader2 size={16} className="animate-spin" />
                      : <Link2 size={16} className="text-indigo-600" />}
                    Regenerate Txn Link
                  </Button>
                )}
              </div>
            </div>
          </div>

          {generatedLink && (
            <TxnLinkCard
              link={generatedLink}
              expiry={linkExpiry}
              copied={linkCopied}
              onCopy={copyLink}
              onDismiss={() => setGeneratedLink(null)}
            />
          )}

          <div className="p-5 mb-4 bg-gray-50 rounded-lg border border-gray-100">
            <h4 className="mb-3 text-sm font-medium text-gray-700 uppercase">
              Customer Details
            </h4>


            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Name</p>
                <p className="font-medium">{logData.data.customerName || 'N/A'}</p>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                <div className="flex items-center">
                  <Phone size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium">{logData.data.customerMobileNo}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Email</p>
                <div className="flex items-center">
                  <Mail size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium truncate" title={logData.data.customerEmail || 'N/A'}>{logData.data.customerEmail || 'N/A'}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">PAN Number</p>
                <div className="flex items-center">
                  <CreditCard size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium">{logData.data.customerPan || 'N/A'}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Date of Birth</p>
                <div className="flex items-center">
                  <Calendar size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium">{formatDateCustomer(logData.data.customerdob)}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Employment Type</p>
                <div className="flex items-center">
                  <Briefcase size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium">{getEmploymentTypeLabel(logData.data.customerEmploymentType)}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Monthly Income</p>
                <div className="flex items-center">
                  <DollarSign size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium">{formatCurrency(logData.data.customerMonthlyIncome)}</p>
                </div>
              </div>

            </div>
          </div>


          {/* Platform Specific Information */}
          <div className="p-5 mb-4 bg-gray-50 rounded-lg border border-gray-100">
            <h4 className="mb-3 text-sm font-medium text-gray-700 uppercase">
              {formatPlatform(logData.data.platform)} Details
            </h4>

            {/* Display data based on platform */}
            {logData.data.platform === 'merchant' && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Merchant Name</p>
                  <p className="font-medium">{logData.data.merchantName || 'N/A'}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                  <div className="flex items-center">
                    <Phone size={14} className="mr-1 text-gray-500" />
                    <p className="font-medium">{logData.data.merchantMobile || 'N/A'}</p>
                  </div>
                </div>
                {logData.data.partnerName && (
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Partner Name</p>
                    <div className="flex items-center">
                      <Building2 size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{logData.data.partnerName}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {logData.data.platform === 'gateway-partner' && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Partner
                  </p>
                  <p className="font-medium">
                    {logData.data.gatewayPartnerName || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Partner Mobile Number
                  </p>
                  <div className="flex items-center">
                    <Phone size={14} className="mr-1 text-gray-500" />
                    <p className="font-medium">
                      {logData.data.gatewayPartnerMobile || 'N/A'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Merchant Name
                  </p>
                  <p className="font-medium">
                    {logData.data.merchantName || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Merchant Mobile Number
                  </p>
                  <div className="flex items-center">
                    <Phone size={14} className="mr-1 text-gray-500" />
                    <p className="font-medium">
                      {logData.data.merchantMobile || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {logData.data.platform === 'customer' && (
              <div>
                {logData.data.note && (
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Note</p>
                    <div className="p-3 bg-white rounded-md border border-gray-200">
                      <div className="flex">
                        <MessageCircle size={16} className="flex-shrink-0 mr-2 mt-0.5 text-blue-500" />
                        <p className="text-gray-700">{logData.data.note}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pre-Approval Details */}
          <div className="p-5 bg-gray-50 rounded-lg border border-gray-100">
            <h4 className="mb-3 text-sm font-medium text-gray-700 uppercase">
              Pre-Approval Details
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {user?.role !== UserRole.CLEVERPE_USER && (
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Lender</p>
                  <p className="font-medium">{selectedLog?.lender || 'N/A'}</p>
                </div>
              )}

              {selectedLog?.preApprovalResponse?.error ? (
                <div className="md:col-span-2 flex flex-col md:flex-row md:items-start gap-6">
                  {/* Error Section */}
                  <div className="flex-1">
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Error Message</p>
                    <div className="flex items-start">
                      <AlertTriangle size={14} className="flex-shrink-0 mr-1 mt-0.5 text-red-500" />
                      <p className="font-medium text-red-600">
                        {selectedLog.preApprovalResponse.error.includes(':')
                          ? selectedLog.preApprovalResponse.error.split(':')[1].trim()
                          : selectedLog.preApprovalResponse.error.replace('Pre-approval failed: ', '')}
                      </p>
                    </div>
                  </div>

                  {/* Remark Section */}
                  {selectedLog?.remark && (
                    <div className="flex-1">
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Reason</p>
                      <p className="font-medium text-gray-700">{selectedLog.remark}</p>
                    </div>
                  )}
                </div>

              ) : (
                <>
                  {/* Status Section */}
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Status</p>
                    <div className="font-medium break-words">
                      {selectedLog?.preApprovalResponse?.status === 'PRE_APPROVAL_OFFER_ALREADY_GENERATED' ? (
                        <>
                          <span className="block">PRE_APPROVAL_OFFER_</span>
                          <span className="block">ALREADY_GENERATED</span>
                          <span className="block">(Approved)</span>
                        </>
                      ) : (
                        <>
                          <span>{selectedLog?.preApprovalResponse?.status || 'N/A'}</span>
                          {selectedLog?.lender?.toLowerCase() === 'zype' &&
                            selectedLog?.preApprovalResponse?.status === 'ACCEPT' && (
                              <span className="block">(Approved)</span>
                            )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Amount Section */}
                  <div>
                    <p className="mt-2 mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      {isCaprion ? 'Approved Amount' : 'Amount'}
                    </p>
                    <p className="font-medium">
                      {displayAmount > 0 ? `₹${displayAmount.toLocaleString('en-IN')}` : 'N/A'}
                    </p>
                  </div>

                  {/* CAPRION IDs */}
                  {isCaprion && (
                    <>
                      <div>
                        <p className="mt-2 mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Application ID</p>
                        <p className="font-medium">{preApproval?.applicationId ?? 'N/A'}</p>
                      </div>
                      <div>
                        <p className="mt-2 mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Applicant ID</p>
                        <p className="font-medium">{preApproval?.applicantId ?? 'N/A'}</p>
                      </div>
                    </>
                  )}

                  {/* Remark Section */}
                  {selectedLog?.remark && (
                    <div>
                      <p className="mt-2 mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Reason</p>
                      <p className="font-medium text-black-700">{selectedLog.remark}</p>
                    </div>
                  )}
                </>
              )}



              <div>
                <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Created At</p>
                <div className="flex items-center">
                  <Clock size={14} className="mr-1 text-gray-500" />
                  <p className="font-medium">
                    {formatDate(selectedLog?.createdAt)}
                  </p>
                </div>
              </div>
              {/* {user?.role !== UserRole.CLEVERPE_USER && (
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">ID</p>
                  <p className="max-w-full text-sm font-medium text-gray-500 truncate" title={selectedLog?._id}>
                    {selectedLog?._id || 'N/A'}
                  </p>
                </div>
              )} */}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default PreApprovalLogDetailModal; 